import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { useLanguage } from '../i18n/LanguageContext';
import { usePageTitleContext } from '../contexts/PageTitleContext';
import featureFlags, { FeatureFlagKey } from '../config/featureFlags';
import { LanguageSwitcherCompact } from './LanguageSwitcher';
import { OfflineIndicator } from './OfflineIndicator';
import { SyncStatusIndicator } from './SyncStatusIndicator';
import VersionBadge from './VersionBadge';
import productionService from '../services/productionService';
import api from '../services/api';
import { hteTransferService } from '../services/hteTransferService';
import { encodeTransferId } from '../utils/transferIdEncoder';
import { showReprintAlertToast, showPackingReplenishmentToast } from './TransferReminderToast';
import { useUnseenNotifications } from '../hooks/useUnseenNotifications';
import { setPrintServiceUrl } from '../services/operatorService';
import { wsService } from '../services/websocket';
import {
  LayoutDashboard,
  Users,
  Package,
  MapPin,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  User,
  Factory,
  ClipboardList,
  TrendingUp,
  Sun,
  Moon,
  Globe,
  Video,
  Activity,
  Truck,
  ChevronLeft,
  ChevronRight,
  Smartphone,
  FileText,
  CalendarDays,
  Clock,
  ListTodo,
  Link2,
  Database,
  HelpCircle,
  ClipboardCheck,
  AlertTriangle,
  Trash2,
  Printer,
  Inbox,
} from 'lucide-react';

const adminNavigationItems: NavigationItem[] = [
  {
    nameKey: 'nav.dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    nameKey: 'nav.users',
    href: '/users',
    icon: Users,
  },
  {
    nameKey: 'nav.inventory',
    href: '/inventory',
    icon: Package,
  },
  {
    nameKey: 'nav.stations',
    href: '/stations',
    icon: Factory,
  },
  {
    nameKey: 'nav.schedule',
    href: '/schedule',
    icon: CalendarDays,
  },
  {
    nameKey: 'nav.reports',
    href: '/supervisor/reports',
    icon: FileText,
  },
  {
    nameKey: 'nav.tutorial',
    href: '/tutorial',
    icon: HelpCircle,
  },
  {
    nameKey: 'nav.settings',
    href: '/settings',
    icon: Settings,
  },
];

const supervisorNavigationItems: NavigationItem[] = [
  {
    nameKey: 'nav.supervisor',
    href: '/supervisor',
    icon: LayoutDashboard,
  },
  {
    nameKey: 'nav.production',
    href: '/production-planning',
    icon: ClipboardList,
  },
  {
    nameKey: 'nav.schedule',
    href: '/schedule',
    icon: CalendarDays,
  },
  {
    nameKey: 'nav.productionProgress',
    href: '/production-progress',
    icon: Activity,
  },
  {
    nameKey: 'nav.cameraManagement',
    href: '/camera-management',
    icon: Video,
    featureFlag: 'CAMERA_INTEGRATION',
  },
  {
    nameKey: 'nav.cameraMonitoringDashboard',
    href: '/supervisor/cameras',
    icon: Video,
    featureFlag: 'CAMERA_INTEGRATION',
  },
  {
    nameKey: 'nav.reports',
    href: '/supervisor/reports',
    icon: FileText,
  },
  {
    nameKey: 'nav.warehouseTransfer',
    href: '/transfers',
    icon: Truck,
  },
  {
    nameKey: 'nav.inventory',
    href: '/inventory',
    icon: Package,
  },
  {
    nameKey: 'nav.tutorial',
    href: '/tutorial',
    icon: HelpCircle,
  },
  {
    nameKey: 'nav.settings',
    href: '/settings',
    icon: Settings,
  },
];

const operatorNavigationItems: NavigationItem[] = [
  {
    nameKey: 'schedule.dailyQueue',
    href: '/schedule?tab=daily',
    icon: ListTodo,
  },
  {
    nameKey: 'schedule.weeklyView',
    href: '/schedule?tab=weekly',
    icon: CalendarDays,
  },
  {
    nameKey: 'nav.production',
    href: '/operator/production',
    icon: Package,
  },
  {
    nameKey: 'nav.tutorial',
    href: '/tutorial',
    icon: HelpCircle,
  },
];

// Manager navigation - Manager Dashboard + supervisor pages
const managerNavigationItems: NavigationItem[] = [
  {
    nameKey: 'nav.dailyGuide',
    href: '/manager/guide',
    icon: ClipboardCheck,
  },
  {
    nameKey: 'nav.managerDashboard',
    href: '/manager',
    icon: LayoutDashboard,
  },
  {
    nameKey: 'nav.schedule',
    href: '/schedule',
    icon: CalendarDays,
  },
  {
    nameKey: 'nav.cameraMonitoringDashboard',
    href: '/supervisor/cameras',
    icon: Video,
    featureFlag: 'CAMERA_INTEGRATION',
  },
  {
    nameKey: 'nav.reports',
    href: '/supervisor/reports',
    icon: FileText,
  },
  {
    nameKey: 'nav.inventory',
    href: '/inventory',
    icon: Package,
  },
  {
    nameKey: 'nav.settings',
    href: '/settings',
    icon: Settings,
  },
];

interface NavigationItem {
  nameKey: string;
  href: string;
  icon: any;
  submenu?: { nameKey: string; href: string }[];
  featureFlag?: FeatureFlagKey; // Optional feature flag requirement
}

// Derive data-tour attribute from nameKey for tutorial highlighting
// e.g. 'nav.dashboard' → 'nav-dashboard', 'schedule.dailyQueue' → 'nav-daily-queue'
function toTourId(nameKey: string): string {
  const key = nameKey.replace(/^(nav|schedule)\./, 'nav-');
  return key.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/\./g, '-');
}

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, language, setLanguage } = useLanguage();
  const { title: pageTitle, mobileTitle, headerTabs } = usePageTitleContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isManagerOrAdmin = user?.role === 'M' || user?.role === 'manager' || user?.role === 'A' || user?.role === 'admin';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Fetch environment info for the wrong-environment warning banner (WCO-2762).
  // Fetched in ALL environments (was local-dev-only) so deployed staging/edge users get warned.
  // The banner itself is hidden on production below — prod is the "correct" default, so a clean
  // prod UI + a loud bar the moment you're anywhere else is the strongest wrong-env signal.
  // WCO-2762 follow-up: the banner shows the env label + dbName (a schema name like 'ics'), but NOT
  // the DB host — displaying an infra IP/hostname to every logged-in user is a sensitivity leak
  // (esp. prod). dbHost is dropped from the shape; dbName stays for the label.
  const { data: envInfo } = useQuery<{ env: string; cloudEnv?: string; envVerified?: boolean; dbName: string }>(
    ['env-info'],
    async () => {
      const { data } = await api.get('/env');
      return data;
    },
    { staleTime: Infinity, refetchOnWindowFocus: false }
  );

  // Fetch transfer reminders for notification bell (manager/admin only)
  const { data: transferReminders = [] } = useQuery(
    ['cancelled-transfer-reminders'],
    () => productionService.getCancelledTransferReminders(),
    { staleTime: 60000, enabled: isManagerOrAdmin }
  );

  // Fetch reprint alerts for notification bell (manager/admin only)
  const { data: reprintAlerts = [] } = useQuery(
    ['reprint-alerts'],
    () => productionService.getReprintAlerts(),
    { staleTime: 60000, enabled: isManagerOrAdmin }
  );

  // Fetch draft inbound transfer reminders (manager/admin only)
  const { data: draftInboundReminders = [] } = useQuery(
    ['draft-inbound-reminders'],
    () => hteTransferService.getDraftInboundReminders(),
    { staleTime: 60000, enabled: isManagerOrAdmin }
  );

  // Fetch packing replenishment alerts (manager/admin only)
  const { data: packingAlerts = [] } = useQuery(
    ['packing-replenishment-alerts'],
    () => productionService.getPackingReplenishmentAlerts(),
    { staleTime: 60000, enabled: isManagerOrAdmin }
  );

  const resolveReminderMutation = useMutation(
    (sessionNumber: string) => productionService.resolveTransferReminder(sessionNumber),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['cancelled-transfer-reminders']);
        toast.success(t('manager.transferReminderResolved') || 'Transfer confirmed deleted');
      },
    }
  );

  const resolveReprintMutation = useMutation(
    (sessionNumber: string) => productionService.resolveReprintAlert(sessionNumber),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['reprint-alerts']);
        toast.success(t('manager.reprintAlertResolved') || 'Reprint alert resolved');
      },
    }
  );

  const notifCount = transferReminders.filter((r: any) => !r.is_dismissed).length + reprintAlerts.length + draftInboundReminders.length + packingAlerts.length;

  // Bell swings until the dropdown is opened, and chimes once per arrival (WCO-2155).
  const hasUnseenNotif = useUnseenNotifications(notifCount, notifOpen);

  // Load print_service_url from config_settings on mount and listen for real-time updates
  useEffect(() => {
    productionService.getConfigSettings().then(settings => {
      const printUrl = settings.find((s: any) => s.setting_key === 'print_service_url');
      if (printUrl) setPrintServiceUrl(printUrl.setting_value);
    }).catch(() => { console.warn('Failed to load print_service_url from config'); });

    const handleConfigUpdate = (data: any) => {
      if (data?.key === 'print_service_url') {
        setPrintServiceUrl(data.value || '');
      }
    };
    wsService.on('config-updated', handleConfigUpdate);

    // Listen for excessive reprint alerts
    const handleReprintAlert = (data: any) => {
      queryClient.invalidateQueries(['reprint-alerts']);
      const packerInfo = data.packer_name ? ` · Packer: ${data.packer_name}` : '';
      showReprintAlertToast(
        t('manager.excessiveReprintsAlert') || 'Excessive label reprints detected',
        `Session ${data.session_number} — ${data.reprint_count} reprints${packerInfo}`
      );
    };
    wsService.on('excessive-reprints', handleReprintAlert);

    // Invalidate transfer reminders bell in real time when a cancellation creates a VB6 reminder
    const handleTransferRemindersUpdated = () => {
      queryClient.invalidateQueries(['cancelled-transfer-reminders']);
    };
    wsService.on('transfer-reminders-updated', handleTransferRemindersUpdated);

    // Invalidate draft inbound reminders when a transfer is created/submitted or a session is cancelled
    const handleDraftRemindersChanged = () => {
      queryClient.invalidateQueries(['draft-inbound-reminders']);
    };
    wsService.on('hte-transfer-updated', handleDraftRemindersChanged);
    wsService.on('hte-transfer-created', handleDraftRemindersChanged);
    wsService.on('production-session-updated', handleDraftRemindersChanged);

    // WCO-2155: auto-replenishment created a draft packing transfer. The bell badge on
    // its own is easy to miss, so raise a persistent popup as well. Only 'created' pops —
    // 'consumed'/'received' fire on every packed box and every receipt, and popping those
    // would bury the manager in noise.
    const handlePackingReplenishment = (data: any) => {
      queryClient.invalidateQueries(['packing-replenishment-alerts']);
      if (data?.type !== 'created' || !isManagerOrAdmin) return;

      const transferNumber: string | null = data.transfer_number || null;
      const items: any[] = Array.isArray(data.items) ? data.items : [];
      const itemSummary = items.map((i: any) => `${i.item_number} ×${i.quantity}`).join(', ');
      const detail = [
        transferNumber ? `${t('common.transfer') || 'Transfer'} ${transferNumber}` : '',
        itemSummary,
      ].filter(Boolean).join(' — ') ||
        (t('admin.packingMaterials.autoTransferCreatedBody') || 'Stock hit its safe level.');

      showPackingReplenishmentToast(
        t('admin.packingMaterials.autoTransferCreatedTitle') || 'Packing material transfer auto-created',
        detail,
        {
          dedupeKey: transferNumber || 'unknown',
          action: transferNumber
            ? {
                label: t('admin.packingMaterials.viewTransfer') || 'View transfer',
                onClick: () => navigate(`/transfers/${encodeTransferId(transferNumber)}`),
              }
            : undefined,
        }
      );
    };
    wsService.on('packing-replenishment', handlePackingReplenishment);

    return () => {
      wsService.off('config-updated', handleConfigUpdate);
      wsService.off('excessive-reprints', handleReprintAlert);
      wsService.off('transfer-reminders-updated', handleTransferRemindersUpdated);
      wsService.off('hte-transfer-updated', handleDraftRemindersChanged);
      wsService.off('hte-transfer-created', handleDraftRemindersChanged);
      wsService.off('production-session-updated', handleDraftRemindersChanged);
      wsService.off('packing-replenishment', handlePackingReplenishment);
    };
  }, [queryClient, t, navigate, isManagerOrAdmin]);

  // Close mobile user menu on click outside
  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Close notification dropdown on click outside
  useEffect(() => {
    if (!notifOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen]);

  // Determine base navigation items based on user role
  // 'S' = Supervisor, 'O' = Operator, 'M' = Manager, 'A' = Admin (or any other role defaults to admin)
  const baseNavigationItems =
    (user?.role === 'S' || user?.role === 'supervisor')
      ? supervisorNavigationItems
      : (user?.role === 'M' || user?.role === 'manager')
        ? managerNavigationItems
        : (user?.role === 'O' || user?.role === 'operator')
          ? operatorNavigationItems
          : adminNavigationItems;

  // Filter and transform navigation items based on feature flags
  const navigationItems = useMemo(() => {
    return baseNavigationItems
      .filter(item => {
        // If no feature flag required, always show
        if (!item.featureFlag) return true;
        // Otherwise, check if the feature flag is enabled
        return featureFlags[item.featureFlag] === true;
      })
      .map(item => {
        // If this is the Inventory item and ITEM_CATALOG is disabled,
        // remove the submenu and link directly to the dashboard
        if (item.nameKey === 'nav.inventory' && item.submenu && !featureFlags.ITEM_CATALOG) {
          return {
            ...item,
            href: '/inventory',
            submenu: undefined,
          };
        }
        return item;
      });
  }, [baseNavigationItems]);

  const handleLogout = () => {
    logout();
  };

  const toggleSubmenu = (itemName: string) => {
    setExpandedMenu(expandedMenu === itemName ? null : itemName);
  };

  const isItemActive = (item: typeof navigationItems[0]) => {
    if (item.submenu) {
      return item.submenu.some(sub => location.pathname === sub.href);
    }
    // Support hrefs with query params (e.g. /schedule?tab=weekly)
    if (item.href.includes('?')) {
      const [path, query] = item.href.split('?');
      if (location.pathname !== path) return false;
      const params = new URLSearchParams(query);
      const searchParams = new URLSearchParams(location.search);
      for (const [key, value] of params.entries()) {
        if (searchParams.get(key) !== value) return false;
      }
      return true;
    }
    return location.pathname === item.href;
  };

  // WCO-2762: wrong-environment warning banner. Shown loudly on the non-production CLOUD
  // environments (staging / dev / local) so they can't be mistaken for prod. HIDDEN — clean, no
  // banner — on the two "correct" environments: verified PRODUCTION, and the on-site EDGE facility
  // stack (which IS the live production floor, so a banner there is noise, not a safety signal).
  //
  // Source of truth = `cloudEnv` (from /api/env), NOT `env`: `env`'s dbName='ics' heuristic MISLABELS
  // a dev VM/tunnel as "staging". `cloudEnv` keys off NODE_ENV (the only per-config signal the base
  // compose forwards into every container: production | staging | development), with
  // DOPPLER_ENVIRONMENT / DOPPLER_CONFIG as secondary local-`doppler run` fallbacks — no VM-IP map.
  // Edge is classified from IS_EDGE (both `env` and `cloudEnv` become 'edge'); we still read it off
  // `env` first so an explicit edge signal always wins.
  //
  // `envVerified` = the identity came from a RECOGNIZED signal (a known NODE_ENV, DOPPLER_ENVIRONMENT
  // in {prd,stg,dev}, DOPPLER_CONFIG containing one of those, or IS_EDGE). When it's FALSE (no
  // recognizable signal — e.g. a localhost tunnel whose only hint is an unrecognized value) we do NOT
  // trust the label: instead of a confident (possibly wrong) env name we show a neutral "unverified
  // environment" warning. For a safety banner, "I'm not sure which env this is" must never read as a
  // known one (and must never silently look like clean verified-prod).
  const resolvedEnv = envInfo?.env === 'edge' ? 'edge' : (envInfo?.cloudEnv || envInfo?.env);
  // Edge is always trusted (driven by IS_EDGE). Otherwise require envVerified.
  // Backward-compat: if the backend predates envVerified (field absent), don't force the unverified
  // state on everything — trust cloudEnv as before. Only an EXPLICIT `false` marks it unverified.
  const isVerified = resolvedEnv === 'edge' || envInfo?.envVerified !== false;
  // Environments that get the loud colored banner. Edge is deliberately EXCLUDED — like production,
  // the on-site facility stack is a "correct" env and stays clean (no banner).
  const isBanneredEnv = !!resolvedEnv && ['staging', 'dev', 'local'].includes(resolvedEnv);

  // Unverified: we have env info but can't trust the classification → neutral safety warning.
  // ALWAYS warn when envVerified is false — do NOT suppress on a 'production' guess: an unverified
  // "production" is itself only a heuristic guess, so hiding the banner would make an unknown env
  // look exactly like verified-prod (clean, no banner) — the precise mistake this feature prevents.
  // The banner is hidden for VERIFIED production AND for EDGE (both "correct" envs: isVerified and
  // not a bannered env).
  const showUnverifiedBanner = !!envInfo && !isVerified;
  // Verified bannered cloud env: the confident, colored, labeled banner.
  const showEnvBanner = isVerified && isBanneredEnv;

  const envBannerColor = resolvedEnv === 'staging' ? 'bg-amber-500'
    : resolvedEnv === 'dev' ? 'bg-purple-600'
    : resolvedEnv === 'local' ? 'bg-blue-500'
    : '';

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-secondary-100 dark:bg-gray-900">
      {/* WCO-3103: version/env badge. On production shows the CalVer release (links to GitHub);
          on other envs shows the env name from the SAME /api/env source as the banner (resolvedEnv)
          so they can't disagree. Follows the sidebar: compact glyph collapsed, full pill expanded. */}
      <VersionBadge sidebarCollapsed={sidebarCollapsed} resolvedEnv={resolvedEnv} envVerified={isVerified} />
      {/* Environment warning banner (WCO-2762) — verified non-prod only */}
      {showEnvBanner && (
        <div className={`${envBannerColor} text-white text-center text-[11px] py-0.5 font-semibold tracking-wide shrink-0 print:hidden`}>
          {(t('common.envBannerPrefix') || 'You are on')} {resolvedEnv!.toUpperCase()} — {envInfo!.dbName}
        </div>
      )}
      {/* Unverified-environment safety warning (WCO-2762) — shown when the env can't be trusted */}
      {showUnverifiedBanner && (
        <div className="bg-slate-700 text-white text-center text-[11px] py-0.5 font-semibold tracking-wide shrink-0 print:hidden">
          ⚠ {(t('common.envBannerUnverified') || 'Unverified environment')} — {envInfo!.dbName}
        </div>
      )}
      <div className="flex-1 flex overflow-hidden">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-secondary-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white dark:bg-gray-800">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              {/* Way Fong Logo */}
              <img
                src="/wf-removebg-preview.png"
                alt="Way Fong"
                className="h-12 w-auto"
              />
            </div>
            <nav className="mt-5 px-2 space-y-1">
              {navigationItems.map((item) => {
                const isActive = isItemActive(item);
                const isExpanded = expandedMenu === item.nameKey || isActive;

                return (
                  <div key={item.nameKey}>
                    {item.submenu ? (
                      <>
                        <button
                          onClick={() => toggleSubmenu(item.nameKey)}
                          className={`${isActive
                            ? 'bg-primary-50 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                            : 'text-secondary-600 hover:bg-secondary-50 dark:text-gray-300 dark:hover:bg-gray-700'
                            } group flex items-center justify-between w-full px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                        >
                          <div className="flex items-center">
                            <item.icon
                              className={`${isActive ? 'text-primary-500 dark:text-primary-400' : 'text-secondary-400 group-hover:text-secondary-500 dark:text-gray-400 dark:group-hover:text-gray-300'
                                } mr-3 h-5 w-5`}
                            />
                            {t(item.nameKey)}
                          </div>
                          <svg
                            className={`h-5 w-5 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        {isExpanded && (
                          <div className="ml-10 mt-1 space-y-1">
                            {item.submenu.map((subItem) => (
                              <Link
                                key={subItem.href}
                                to={subItem.href}
                                className={`${location.pathname === subItem.href
                                  ? 'text-primary-600 font-medium dark:text-primary-400'
                                  : 'text-secondary-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400'
                                  } block px-2 py-1 text-sm rounded-md transition-colors duration-200`}
                                onClick={() => setSidebarOpen(false)}
                              >
                                {t(subItem.nameKey)}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className={`${isActive
                          ? 'bg-primary-50 text-primary-600 border-r-2 border-primary-600 dark:bg-primary-900 dark:text-primary-300 dark:border-primary-400'
                          : 'text-secondary-600 hover:bg-secondary-50 dark:text-gray-300 dark:hover:bg-gray-700'
                          } group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors duration-200`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <item.icon
                          className={`${isActive ? 'text-primary-500 dark:text-primary-400' : 'text-secondary-400 group-hover:text-secondary-500 dark:text-gray-400 dark:group-hover:text-gray-300'
                            } mr-4 h-6 w-6`}
                        />
                        {t(item.nameKey)}
                      </Link>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className={`flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'}`}>
          <div className="flex flex-col h-0 flex-1 bg-white dark:bg-gray-800 border-r border-secondary-200 dark:border-gray-700 relative">
            {/* Collapse toggle button */}
            <button
              onClick={() => { const next = !sidebarCollapsed; localStorage.setItem('sidebarCollapsed', String(next)); setSidebarCollapsed(next); }}
              className="absolute -right-3 top-20 z-10 w-6 h-6 bg-white dark:bg-gray-800 border border-secondary-200 dark:border-gray-700 rounded-full flex items-center justify-center text-secondary-400 hover:text-secondary-600 dark:text-gray-400 dark:hover:text-gray-200 shadow-sm transition-colors"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>

            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto overflow-x-hidden">
              <div className={`flex items-center flex-shrink-0 ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'}`}>
                {/* Way Fong Logo */}
                <img
                  src="/wf-removebg-preview.png"
                  alt="Way Fong"
                  className={`transition-all duration-300 ${sidebarCollapsed ? 'h-10 w-auto' : 'h-16 w-auto'}`}
                />
              </div>
              <nav className="mt-8 flex-1 px-2 space-y-1">
                {navigationItems.map((item) => {
                  const isActive = isItemActive(item);
                  const isExpanded = expandedMenu === item.nameKey || isActive;

                  return (
                    <div key={item.nameKey}>
                      {item.submenu ? (
                        <>
                          <button
                            onClick={() => !sidebarCollapsed && toggleSubmenu(item.nameKey)}
                            className={`${isActive
                              ? 'bg-primary-50 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                              : 'text-secondary-600 hover:bg-secondary-50 dark:text-gray-300 dark:hover:bg-gray-700'
                              } group flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} w-full px-2 py-2 text-base font-medium rounded-md transition-colors duration-200`}
                            title={sidebarCollapsed ? t(item.nameKey) : undefined}
                          >
                            <div className="flex items-center">
                              <item.icon
                                className={`${isActive ? 'text-primary-500 dark:text-primary-400' : 'text-secondary-400 group-hover:text-secondary-500 dark:text-gray-400 dark:group-hover:text-gray-300'
                                  } ${sidebarCollapsed ? '' : 'mr-4'} h-6 w-6 flex-shrink-0`}
                              />
                              {!sidebarCollapsed && <span className="truncate">{t(item.nameKey)}</span>}
                            </div>
                            {!sidebarCollapsed && (
                              <svg
                                className={`h-5 w-5 transform transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                          {isExpanded && !sidebarCollapsed && (
                            <div className="ml-8 mt-1 space-y-1">
                              {item.submenu.map((subItem) => (
                                <Link
                                  key={subItem.href}
                                  to={subItem.href}
                                  className={`${location.pathname === subItem.href
                                    ? 'text-primary-600 font-medium dark:text-primary-400'
                                    : 'text-secondary-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400'
                                    } block px-2 py-1 text-sm rounded-md transition-colors duration-200 truncate`}
                                >
                                  {t(subItem.nameKey)}
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <Link
                          to={item.href}
                          data-tour={toTourId(item.nameKey)}
                          className={`${isActive
                            ? 'bg-primary-50 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                            : 'text-secondary-600 hover:bg-secondary-50 dark:text-gray-300 dark:hover:bg-gray-700'
                            } group flex items-center ${sidebarCollapsed ? 'justify-center' : ''} px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                          title={sidebarCollapsed ? t(item.nameKey) : undefined}
                        >
                          <item.icon
                            className={`${isActive ? 'text-primary-500 dark:text-primary-400' : 'text-secondary-400 group-hover:text-secondary-500 dark:text-gray-400 dark:group-hover:text-gray-300'
                              } ${sidebarCollapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0`}
                          />
                          {!sidebarCollapsed && <span className="truncate">{t(item.nameKey)}</span>}
                        </Link>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-secondary-200 dark:border-gray-700">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                className="md:hidden -ml-2 mr-2 h-6 w-6 inline-flex items-center justify-center rounded-md text-secondary-400 hover:text-secondary-500 dark:text-gray-400 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
              {/* Page Title — on mobile, headerTabs replaces the title when present */}
              {headerTabs ? (
                <>
                  <h1 className="hidden sm:block ml-2 md:ml-0 text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {pageTitle}
                  </h1>
                  <div className="sm:hidden ml-2">{headerTabs}</div>
                </>
              ) : pageTitle ? (
                <h1 className="ml-2 md:ml-0 text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">
                  <span className="hidden sm:inline">{pageTitle}</span>
                  <span className="sm:hidden">{mobileTitle || pageTitle}</span>
                </h1>
              ) : null}
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Sync & offline status indicators */}
              <SyncStatusIndicator />
              <OfflineIndicator />

              {/* Theme toggle - desktop only */}
              <button
                onClick={toggleTheme}
                className="hidden md:inline-flex p-2 text-secondary-400 hover:text-secondary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md transition-colors"
                title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </button>

              {/* Language Switcher - desktop only */}
              <div className="hidden md:block">
                <LanguageSwitcherCompact />
              </div>

              {/* Notifications - desktop only, manager/admin only */}
              {isManagerOrAdmin && <div className="hidden md:block relative" ref={notifRef}>
                <button
                  onClick={() => setNotifOpen(!notifOpen)}
                  className="relative p-2 text-secondary-400 hover:text-secondary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md"
                >
                  {/* origin-top so it pivots like a bell rather than spinning about its
                      middle. motion-reduce disables the swing for users who ask the OS
                      to minimise motion — the badge still conveys the same information. */}
                  <Bell className={`h-5 w-5 origin-top ${hasUnseenNotif ? 'animate-bell-swing motion-reduce:animate-none' : ''}`} />
                  {notifCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {notifCount}
                    </span>
                  )}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{t('nav.notifications') || 'Notifications'}</h3>
                    </div>
                    {transferReminders.length === 0 && reprintAlerts.length === 0 && draftInboundReminders.length === 0 && packingAlerts.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        {t('nav.noNotifications') || 'No notifications'}
                      </div>
                    ) : (
                      <div className="max-h-80 overflow-y-auto">
                        {/* Packing replenishment alerts */}
                        {packingAlerts.map((a: any) => (
                          <div key={`packing-${a.transfer_number}`} className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
                            <div className="flex items-start gap-3">
                              <Package className="w-4 h-4 text-purple-500 shrink-0 mt-0.5" />
                              <button
                                onClick={() => {
                                  setNotifOpen(false);
                                  navigate(`/transfers/${encodeTransferId(a.transfer_number)}`);
                                }}
                                className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                              >
                                <p className="text-xs font-medium text-purple-600 dark:text-purple-400 mb-0.5">
                                  {a.status === 'draft'
                                    ? (t('admin.packingMaterials.draftTransferAlert') || 'Packing material replenishment — review & submit')
                                    : (t('admin.packingMaterials.pendingTransferAlert') || 'Packing material replenishment submitted')
                                  }
                                </p>
                                <span className="text-xs text-purple-600 dark:text-purple-400">
                                  {t('common.transfer') || 'Transfer'} <span className="font-mono font-medium">{a.transfer_number}</span>
                                </span>
                                <span className="text-xs text-gray-400 mx-1">·</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{a.source_warehouse} → HTE</span>
                                {a.items && (
                                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                    {a.items.map((item: any, i: number) => (
                                      <span key={i} className="text-xs text-gray-500 dark:text-gray-400">
                                        <span className="font-mono">{item.item_number}</span>
                                        {' '}<span className="font-medium">{Number(item.quantity).toLocaleString()}</span> pcs
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                        {/* Draft inbound transfer reminders */}
                        {draftInboundReminders.map((d: any) => (
                          <div key={`draft-${d.transfer_number}-${d.session_number || 'no-session'}`} className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
                            <div className="flex items-start gap-3">
                              <Inbox className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                              <button
                                onClick={() => {
                                  setNotifOpen(false);
                                  navigate(`/transfers/${encodeTransferId(d.transfer_number)}`);
                                }}
                                className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                              >
                                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-0.5">
                                  {t('manager.draftTransferReminder') || 'Transfer to HTE not yet submitted'}
                                </p>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs text-blue-600 dark:text-blue-400">
                                    {t('common.transfer') || 'Transfer'} <span className="font-mono font-medium">{d.transfer_number}</span>
                                  </span>
                                  {d.session_number && (
                                    <>
                                      <span className="text-xs text-gray-400">·</span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {t('common.session') || 'Session'} <span className="font-mono font-medium text-gray-700 dark:text-gray-300">{d.session_number}</span>
                                      </span>
                                    </>
                                  )}
                                  {(d.warehouse_code_from || d.warehouse_code_to) && (
                                    <>
                                      <span className="text-xs text-gray-400">·</span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {d.warehouse_code_from || '?'} → {d.warehouse_code_to || '?'}
                                      </span>
                                    </>
                                  )}
                                </div>
                                {d.product_name && (
                                  <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">{d.product_name}</p>
                                )}
                                {d.materials && (
                                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                    {d.materials.map((m: any, i: number) => (
                                      <span key={i} className="text-xs text-gray-500 dark:text-gray-400">
                                        <span className="font-mono">{m.item_number}</span>
                                        {m.cases > 0 && <>{' '}<span className="font-medium">{Number(m.cases).toFixed(0)}</span> cs</>}
                                        {m.weight > 0 && <>{' · '}<span className="font-medium">{Number(m.weight).toFixed(2)}</span> lbs</>}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                        {/* Reprint alerts */}
                        {reprintAlerts.map((a: any) => (
                          <div key={`reprint-${a.session_number}`} className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
                            <div className="flex items-start gap-3">
                              <Printer className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                              <button
                                onClick={() => {
                                  setNotifOpen(false);
                                  navigate(`/production-planning?session=${a.session_number}`);
                                }}
                                className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                              >
                                <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-0.5">
                                  {t('manager.excessiveReprintsAlert') || 'Excessive label reprints detected'}
                                </p>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  Session {a.session_number} — {a.product_name || a.product_sku}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {a.reprint_count} {t('manager.reprints') || 'reprints'}
                                  {a.packer_name && <span className="ml-2">· {t('supervisor.packer') || 'Packer'}: {a.packer_name}</span>}
                                </p>
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); resolveReprintMutation.mutate(a.session_number); }}
                                disabled={resolveReprintMutation.isLoading}
                                className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 dark:text-green-400 dark:bg-green-900/30 dark:hover:bg-green-900/50 rounded shrink-0"
                                title={t('manager.markResolved') || 'Mark as resolved'}
                              >
                                {t('manager.markResolved') || 'Resolve'}
                              </button>
                            </div>
                          </div>
                        ))}
                        {/* Transfer reminders */}
                        {transferReminders.map((r: any) => (
                          <div key={`transfer-${r.session_number}-${r.transfer_number}`} className={`px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 ${r.is_dismissed ? 'opacity-50' : ''}`}>
                            <div className="flex items-start gap-3">
                              <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${r.is_dismissed ? 'text-gray-400' : 'text-amber-500'}`} />
                              <button
                                onClick={() => {
                                  setNotifOpen(false);
                                  navigate(`/transfers/${encodeTransferId(r.transfer_number)}`);
                                }}
                                className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                              >
                                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-0.5">
                                  {t('sessionDetails.vb6TransferReminder') || 'Remember to delete transfer in VB6'}
                                </p>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">Session <span className="font-mono font-medium text-gray-700 dark:text-gray-300">{r.session_number}</span></span>
                                  <span className="text-xs text-gray-400">·</span>
                                  <span className="text-xs text-amber-600 dark:text-amber-400">Transfer <span className="font-mono font-medium">{r.transfer_number}</span></span>
                                  {r.is_dismissed && <span className="text-[10px] text-gray-400 italic">({t('common.dismissed') || 'dismissed'})</span>}
                                </div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">{r.product_name || r.product_sku}</p>
                                {r.transfer_items && (
                                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                    {r.transfer_items.map((item: any, i: number) => (
                                      <span key={i} className="text-xs text-gray-500 dark:text-gray-400">
                                        <span className="font-mono">{item.item_number}</span>
                                        {' '}<span className="font-medium">{Number(item.cases).toFixed(0)}</span> cs
                                        {' · '}<span className="font-medium">{Number(item.weight).toFixed(2)}</span> lbs
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); resolveReminderMutation.mutate(r.session_number); }}
                                disabled={resolveReminderMutation.isLoading}
                                className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 dark:text-green-400 dark:bg-green-900/30 dark:hover:bg-green-900/50 rounded shrink-0"
                                title={t('manager.markResolved') || 'Mark as resolved'}
                              >
                                {t('manager.markResolved') || 'Resolve'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>}

              {/* User menu - Desktop */}
              <div className="hidden md:flex items-center relative">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                      <User className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-secondary-900 dark:text-gray-100">
                      {user?.firstName && user?.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user?.username
                      }
                    </div>
                    <div className="text-xs text-secondary-500 dark:text-gray-400 capitalize">
                      {user?.role}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-4 p-2 text-secondary-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded-md transition-colors duration-200"
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>

              {/* User menu - Mobile (avatar dropdown) */}
              <div className="md:hidden relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex-shrink-0"
                >
                  <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                    <User className="h-5 w-5 text-white" />
                  </div>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 border border-secondary-200 dark:border-gray-700 z-50">
                    <div className="px-4 py-3 border-b border-secondary-200 dark:border-gray-700">
                      <div className="text-sm font-medium text-secondary-900 dark:text-gray-100">
                        {user?.firstName && user?.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user?.username
                        }
                      </div>
                      <div className="text-xs text-secondary-500 dark:text-gray-400 capitalize">
                        {user?.role}
                      </div>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={() => { toggleTheme(); setUserMenuOpen(false); }}
                        className="w-full flex items-center px-4 py-2 text-sm text-secondary-700 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700"
                      >
                        {theme === 'dark' ? <Sun className="h-4 w-4 mr-3" /> : <Moon className="h-4 w-4 mr-3" />}
                        {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                      </button>
                      <button
                        onClick={() => {
                          const next = language === 'en' ? 'zh-TW' : language === 'zh-TW' ? 'es' : 'en';
                          setLanguage(next as any);
                          setUserMenuOpen(false);
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-secondary-700 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700"
                      >
                        <Globe className="h-4 w-4 mr-3" />
                        Language
                      </button>
                      <button
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center px-4 py-2 text-sm text-secondary-700 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700"
                      >
                        <Bell className="h-4 w-4 mr-3" />
                        Notifications
                      </button>
                    </div>
                    <div className="border-t border-secondary-200 dark:border-gray-700">
                      <button
                        onClick={() => { handleLogout(); setUserMenuOpen(false); }}
                        className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-secondary-100 dark:hover:bg-gray-700 rounded-b-md"
                      >
                        <LogOut className="h-4 w-4 mr-3" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="w-full py-4 px-4 sm:px-6 md:px-8 min-h-full flex flex-col">
            {children}
          </div>
        </main>
      </div>
      </div>
    </div>
  );
};

export default Layout;